import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiPause, FiSkipForward, FiSettings, FiWifi, FiClock, FiImage, FiVideo } = FiIcons;

const TVPlayer = () => {
  const { monitorId } = useParams();
  const [currentMedia, setCurrentMedia] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');

  const playlist = [
    {
      id: 1,
      type: 'image',
      name: 'Welcome Message',
      url: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=1920&h=1080&fit=crop',
      duration: 10000 // 10 seconds for images
    },
    {
      id: 2,
      type: 'video',
      name: 'Product Showcase',
      url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=1920&h=1080&fit=crop',
      duration: 30000 // 30 seconds for videos
    },
    {
      id: 3,
      type: 'image',
      name: 'Company Info',
      url: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=1920&h=1080&fit=crop',
      duration: 8000
    },
    {
      id: 4,
      type: 'image',
      name: 'Special Offer',
      url: 'https://images.unsplash.com/photo-1551818255-e6e10975bc17?w=1920&h=1080&fit=crop',
      duration: 12000
    }
  ];

  useEffect(() => {
    if (!isPlaying) return;

    const timer = setTimeout(() => {
      setCurrentMedia((prev) => (prev + 1) % playlist.length);
    }, playlist[currentMedia].duration);

    return () => clearTimeout(timer);
  }, [currentMedia, isPlaying, playlist]);

  const handleNext = () => {
    setCurrentMedia((prev) => (prev + 1) % playlist.length);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const currentItem = playlist[currentMedia];

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Main Content Display */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentMedia}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0"
        >
          <img
            src={currentItem.url}
            alt={currentItem.name}
            className="w-full h-full object-cover"
          />
          
          {/* Content Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/30">
            {/* Top Status Bar */}
            <div className="absolute top-0 left-0 right-0 p-6">
              <div className="flex justify-between items-center text-white">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
                    <SafeIcon icon={FiWifi} className="text-green-400" />
                    <span className="text-sm">Connected</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
                    <SafeIcon icon={FiClock} />
                    <span className="text-sm">{new Date().toLocaleTimeString()}</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
                  <SafeIcon icon={currentItem.type === 'video' ? FiVideo : FiImage} />
                  <span className="text-sm">{currentItem.name}</span>
                </div>
              </div>
            </div>

            {/* Bottom Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="bg-black/50 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-sm">
                    {currentMedia + 1} of {playlist.length}
                  </span>
                  <span className="text-white/70 text-sm">
                    {isPlaying ? 'Playing' : 'Paused'}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-white/20 rounded-full h-2 mb-3">
                  <motion.div
                    className="bg-gradient-to-r from-purple-400 to-pink-400 h-2 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: isPlaying ? '100%' : '0%' }}
                    transition={{ duration: currentItem.duration / 1000, ease: 'linear' }}
                  />
                </div>

                {/* Playlist Items */}
                <div className="flex space-x-2 overflow-x-auto">
                  {playlist.map((item, index) => (
                    <div
                      key={item.id}
                      className={`flex-shrink-0 w-16 h-10 rounded-lg overflow-hidden border-2 ${
                        index === currentMedia ? 'border-purple-400' : 'border-white/20'
                      }`}
                    >
                      <img
                        src={item.url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Controls Overlay */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6 text-center">Player Controls</h2>
              
              <div className="flex items-center justify-center space-x-6 mb-8">
                <button
                  onClick={handlePlayPause}
                  className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
                >
                  <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-white text-2xl" />
                </button>
                
                <button
                  onClick={handleNext}
                  className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <SafeIcon icon={FiSkipForward} className="text-white text-xl" />
                </button>
              </div>

              <div className="flex space-x-4">
                <Link
                  to="/tv/setup"
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white/20 border border-white/30 text-white rounded-xl hover:bg-white/30 transition-colors"
                >
                  <SafeIcon icon={FiSettings} />
                  <span>Settings</span>
                </Link>
                
                <button
                  onClick={() => setShowControls(false)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click Area for Controls */}
      <button
        onClick={() => setShowControls(!showControls)}
        className="absolute inset-0 w-full h-full bg-transparent focus:outline-none"
        aria-label="Show controls"
      />

      {/* Connection Status Indicator */}
      <div className="absolute top-6 right-6">
        <div className={`w-3 h-3 rounded-full ${
          connectionStatus === 'connected' ? 'bg-green-400' :
          connectionStatus === 'connecting' ? 'bg-yellow-400 animate-pulse' :
          'bg-red-400'
        }`}></div>
      </div>
    </div>
  );
};

export default TVPlayer;